import { Box, Paper, styled, Typography, FormControl, Select, MenuItem } from "@mui/material";
import React from "react";
import Chart from "react-chartjs-2";
import { useTranslation } from "react-i18next";

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "none",
  transition: "box-shadow 0.3s ease-in-out",
  border: '1px solid #D9D9D9',
  height: '100%',
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  },
}));

const CardHeaderWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const CardBodyWrapper = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  height: "70%",
  width: "100%",
  position: "relative",
}));

function ApplicationTrafficChart() {
  const { t } = useTranslation();
  // Mock data to preview the chart UI
  const mockData = {
    labels: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ],
    datasets: [
      {
        label: t('superAdmin.applicationTrafficChart.applications'),
        data: [260, 380, 380, 500, 550, 500, 5, 670],
        backgroundColor: "#BE84D9",
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  };

  // Timeframe for last N months (6 or 12)
  const [timeframe, setTimeframe] = React.useState('6');
  const [serverData, setServerData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = React.useState(currentYear);

  React.useEffect(() => {
    const fetchTraffic = async () => {
      try {
        if (!apiUrl || !token) return;
        setLoading(true);
        const res = await fetch(`${apiUrl}authentication/superadmin/application-traffic/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setServerData(json);
        if (json && Array.isArray(json.years) && json.years.length > 0) {
          if (json.years.includes(currentYear)) {
            setSelectedYear(currentYear);
          } else {
            setSelectedYear(json.years[0]);
          }
        }
      } catch (e) {
        console.error('Failed to load application traffic', e);
      } finally {
        setLoading(false);
      }
    };
    fetchTraffic();
  }, [apiUrl, token]);

  React.useEffect(() => {
    try {
      const wsBase = (apiUrl || '').replace(/^http/, 'ws');
      if (!wsBase) return;
      const socket = new WebSocket(`${wsBase.replace(/\/$/, '')}/ws/applications/traffic/`);
      socket.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data || '{}');
          if (data.type === 'application_traffic_update') {
            (async () => {
              try {
                const res = await fetch(`${apiUrl}authentication/superadmin/application-traffic/`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                const json = await res.json();
                setServerData(json);
              } catch (e) {
                console.error('WS refresh failed', e);
              }
            })();
          }
        } catch (_) {}
      };
      return () => socket.close();
    } catch (_) {}
  }, [apiUrl, token]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#0f172a',
          font: { family: "'Inter', sans-serif", size: 12 },
          usePointStyle: true,
        }
      },
      tooltip: {
        backgroundColor: "#000",
        titleColor: "#fff",
        bodyColor: "#fff",
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 12,
        },
        padding: 12,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          label: (context) => `${context.raw} ${context.dataset.label}`,
        },
        responsive: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          padding: 8,
          color: "#64748B",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
          maxRotation: 45,
          minRotation: 45,
        },
        responsive: true,
      },
      y: {
        border: {
          display: false,
        },
        grid: {
          color: "#E2E8F0",
          lineWidth: 0.5,
        },
        ticks: {
          padding: 8,
          color: "#64748B",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
          stepSize: 100,
          callback: function(value) {
            return value >= 1000 ? (value/1000).toFixed(1) + 'k' : value;
          }
        },
        beginAtZero: true,
        responsive: true,
      },
    },
    elements: {
      line: {
        tension: 0.35,
        borderWidth: 2,
        fill: false,
      },
      point: {
        radius: 2.5,
        hoverRadius: 4,
        hitRadius: 10,
      }
    },
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  // Create a vertical gradient: strong at top, fading to bottom
  const getAreaGradient = (ctx, chartArea) => {
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    // Strong at the top, degrade towards the bottom
    gradient.addColorStop(0, 'rgba(190, 132, 217, 1.0)');
    gradient.addColorStop(0.6, 'rgba(190, 132, 217, 0.65)');
    gradient.addColorStop(1, 'rgba(190, 132, 217, 0.15)');
    return gradient;
  };

  // Shadow effect removed to render a clean line without top glow

  const filterLastSixMonths = (data) => {
    if (!data) return null;

    const monthNames = [
      t("superAdmin.applicationTrafficChart.months.january"),
      t("superAdmin.applicationTrafficChart.months.february"),
      t("superAdmin.applicationTrafficChart.months.march"),
      t("superAdmin.applicationTrafficChart.months.april"),
      t("superAdmin.applicationTrafficChart.months.may"),
      t("superAdmin.applicationTrafficChart.months.june"),
      t("superAdmin.applicationTrafficChart.months.july"),
      t("superAdmin.applicationTrafficChart.months.august"),
      t("superAdmin.applicationTrafficChart.months.september"),
      t("superAdmin.applicationTrafficChart.months.october"),
      t("superAdmin.applicationTrafficChart.months.november"),
      t("superAdmin.applicationTrafficChart.months.december")
    ];
    const originalMonthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const monthsToShow = Number(timeframe) === 12 ? 12 : 6;
    const currentMonth = new Date().getMonth();

    // New payload shape path
    if (serverData?.data_by_year && serverData?.years) {
      const yearData = serverData.data_by_year[String(selectedYear)] || Array(12).fill(0);
      let usedLabels = [];
      let usedData = [];

      if (monthsToShow === 12) {
        usedLabels = monthNames;
        usedData = yearData;
      } else {
        if (selectedYear === new Date().getFullYear()) {
          // Last 6 months ending at current month within current year
          const indices = [];
          for (let i = monthsToShow - 1; i >= 0; i--) {
            indices.push((currentMonth - i + 12) % 12);
          }
          usedLabels = indices.map(i => monthNames[i]);
          usedData = indices.map(i => yearData[i] || 0);
        } else {
          // Historical year: use last 6 months of that calendar year (Jul..Dec)
          const indices = [6,7,8,9,10,11];
          usedLabels = indices.map(i => monthNames[i]);
          usedData = indices.map(i => yearData[i] || 0);
        }
      }

      return {
        labels: usedLabels,
        datasets: [
          {
            label: t('superAdmin.applicationTrafficChart.applications'),
            type: 'line',
            data: usedData,
            borderColor: '#BE84D9',
            backgroundColor: (context) => {
              const { chart } = context;
              const { ctx, chartArea } = chart || {};
              if (!chartArea) return 'rgba(190, 132, 217, 0.4)';
              return getAreaGradient(ctx, chartArea);
            },
            fill: true,
            borderWidth: 3,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: '#BE84D9',
          }
        ]
      };
    }

    // Backward-compat: old payload shape
    if (!data.labels || !data.datasets) return null;

    const lastMonths = [];
    for (let i = monthsToShow - 1; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      lastMonths.push(monthNames[monthIndex]);
    }

    const totalsByMonth = lastMonths.map(translatedMonth => {
      const originalMonthName = originalMonthNames[monthNames.indexOf(translatedMonth)];
      const monthIndex = data.labels.indexOf(originalMonthName);
      if (monthIndex === -1) return 0;
      return data.datasets.reduce((sum, ds) => sum + (Number(ds.data[monthIndex]) || 0), 0);
    });

    return {
      labels: lastMonths,
      datasets: [
        {
          label: t('superAdmin.applicationTrafficChart.applications'),
          type: 'line',
          data: totalsByMonth,
          borderColor: '#BE84D9',
          backgroundColor: (context) => {
            const { chart } = context;
            const { ctx, chartArea } = chart || {};
            if (!chartArea) return 'rgba(190, 132, 217, 0.4)';
            return getAreaGradient(ctx, chartArea);
          },
          fill: true,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: '#BE84D9',
        }
      ]
    };
  };

  // Adapt to two payload shapes:
  // 1) Old: { labels, datasets }
  // 2) New: { labels, years, data_by_year }
  const normalizePayload = (payload) => {
    if (!payload) return null;
    if (payload.data_by_year && payload.years) {
      const yearKey = String(selectedYear);
      return {
        labels: payload.labels,
        datasets: [
          {
            label: t('superAdmin.applicationTrafficChart.applications'),
            data: payload.data_by_year[yearKey] || Array(12).fill(0),
          }
        ]
      };
    }
    return payload;
  };

  const filteredData = filterLastSixMonths(normalizePayload(serverData) || mockData);

  const totalForSelectedYear = React.useMemo(() => {
    try {
      if (serverData?.data_by_year && serverData?.years) {
        const arr = serverData.data_by_year[String(selectedYear)] || [];
        return arr.reduce((sum, v) => sum + (Number(v) || 0), 0);
      }
      // Fallback: sum all months from mock dataset
      const monthlyTotals = Array(12).fill(0);
      (mockData.datasets || []).forEach(ds => {
        (ds.data || []).forEach((v, i) => {
          monthlyTotals[i] = (monthlyTotals[i] || 0) + (Number(v) || 0);
        });
      });
      return monthlyTotals.reduce((a, b) => a + b, 0);
    } catch (_) {
      return 0;
    }
  }, [serverData, selectedYear]);

  if (!filteredData) {
    const currentMonth = new Date().getMonth();
    const monthNames = [
      t("superAdmin.applicationTrafficChart.months.january"),
      t("superAdmin.applicationTrafficChart.months.february"),
      t("superAdmin.applicationTrafficChart.months.march"),
      t("superAdmin.applicationTrafficChart.months.april"),
      t("superAdmin.applicationTrafficChart.months.may"),
      t("superAdmin.applicationTrafficChart.months.june"),
      t("superAdmin.applicationTrafficChart.months.july"),
      t("superAdmin.applicationTrafficChart.months.august"),
      t("superAdmin.applicationTrafficChart.months.september"),
      t("superAdmin.applicationTrafficChart.months.october"),
      t("superAdmin.applicationTrafficChart.months.november"),
      t("superAdmin.applicationTrafficChart.months.december")
    ];
  
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      last6Months.push(monthNames[monthIndex]);
    }
  
    const defaultData = {
      labels: last6Months,
      datasets: [
        {
          label: t('superAdmin.applicationTrafficChart.noDataAvailable'),
          data: [0, 0, 0, 0, 0, 0],
          backgroundColor: "#E2E8F0",
          borderWidth: 0,
          borderRadius: 40,
        },
      ],
    };
  
    return (
      <div className="w-full h-full">
        <Chart
          type="bar"
          data={defaultData}
          options={{
            ...chartOptions,
            maintainAspectRatio: false,
            responsive: true,
          }}
          style={{ height: "100%", width: "100%", padding: "1rem" }}
        />
      </div>
    );
  }

  return (
    <StyledCard elevation={0}>
      <CardHeaderWrapper>
        <Box
         display="flex"
         alignItems="center"
         justifyContent="space-between"
         width="100%"
        >
          <Box
            display="flex" alignItems="center"
          >
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: { xs: '14px', sm: '16px' },
                color: '#00000080'
              }}
            >
              {t('superAdmin.applicationTrafficChart.applicationTrafficTotal')} — {selectedYear}: {totalForSelectedYear?.toLocaleString?.() || 0}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                <MenuItem value={'6'}>{t('superAdmin.applicationTrafficChart.last6Months')}</MenuItem>
                <MenuItem value={'12'}>{t('superAdmin.applicationTrafficChart.last12Months')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {(serverData?.years || [currentYear]).map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </CardHeaderWrapper>
      <CardBodyWrapper>
        <Box sx={{ 
          height: { xs: 250, sm: 300, md: 350 },
          width: '100%',
          position: 'relative',
          '& canvas': {
            maxWidth: '100% !important',
            height: '90% !important'
          }
        }}>
          <Chart
            type="line"
            data={filteredData}
            options={{
              ...chartOptions,
              maintainAspectRatio: false,
              responsive: true,
            }}
          />
        </Box>
      </CardBodyWrapper>
    </StyledCard>
  );
}

export default ApplicationTrafficChart;

